import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import logger from '../lib/logger';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { cn } from '../lib/utils';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, ComposedChart, Bar, Cell, ReferenceLine
} from 'recharts';
import { Activity, AlertTriangle, TrendingUp, TrendingDown, CheckCircle2, Target, Sparkles, Wrench, FileText, Mail, Brain, Loader2, ChevronRight, Info, Percent, BarChart2, Shuffle } from 'lucide-react';
import { toast } from 'sonner';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

function FormattedNarrative({ text }) {
  if (!text) return null;
  const lines = text.split('\n').filter(l => l.trim());
  const elements = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (bulletBuffer.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} className="list-disc pl-5 space-y-1 my-2">
          {bulletBuffer.map((b, i) => <li key={`li-${elements.length}-${i}`}>{renderBold(b)}</li>)}
        </ul>
      );
      bulletBuffer = [];
    }
  };

  const renderBold = (str) => {
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`b-${i}-${part.slice(2, 6)}`}>{part.slice(2, -2)}</strong>;
      }
      return <span key={`t-${i}`}>{part}</span>;
    });
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.startsWith('* ')) {
      bulletBuffer.push(trimmed.replace(/^[-•*]\s*/, ''));
    } else if (/^\d+[.)]\s/.test(trimmed)) {
      bulletBuffer.push(trimmed.replace(/^\d+[.)]\s*/, ''));
    } else {
      flushBullets();
      // Detect section headings: short lines ending with ':'
      const isHeading = trimmed.endsWith(':') && trimmed.length < 80 && !trimmed.startsWith('**');
      if (isHeading) {
        elements.push(<p key={`h-${elements.length}`} className="mb-1 mt-3 first:mt-0 font-bold text-slate-800">{trimmed}</p>);
      } else {
        elements.push(<p key={`p-${elements.length}`} className="mb-2">{renderBold(trimmed)}</p>);
      }
    }
  }
  flushBullets();
  return <>{elements}</>;
}

const MEASURE_LABELS = {
  certification_depth: 'Certification Depth',
  community_learning: 'Community Learning',
  scheduling_velocity: 'Scheduling Velocity',
  training_scope: 'Training Scope',
  sme_training: 'SME Training',
  voluntary_engagement: 'Voluntary Engagement',
  tech_utilization: 'Tech Utilization',
  learning_culture: 'Learning Culture',
  training_performance: 'Training Performance',
  nps: 'NPS',
  skills_assessment: 'Skills Assessment',
  skills_coverage: 'Skills Coverage',
  readiness_index: 'Readiness Index',
  cost_efficiency: 'Cost Efficiency (LEI)',
  kpi_alignment: 'KPI Alignment',
  pe_capability_capability_score: 'PE Capability',
  pe_motivation_total_score: 'PE Motivation',
  pe_retention_retention_rate: 'PE Retention',
  pe_lei_lei_score: 'PE LEI Score',
};

const KPI_LABELS = {
  engagement: 'Employee Engagement',
  quality: 'Quality / Error Reduction',
  safety: 'Safety Incidents',
  strategic_init: 'Strategic Initiatives',
  speed_market: 'Speed to Market',
  cost_efficiency: 'Cost Efficiency (KPI)',
  productivity: 'Productivity',
  csat: 'Customer Satisfaction',
  scalability: 'Scalability',
  mobility: 'Talent Mobility',
};

function getLabel(id) {
  return MEASURE_LABELS[id] || KPI_LABELS[id] || id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function getCorrelationColor(r) {
  const abs = Math.abs(r);
  if (abs >= 0.7) return r > 0 ? '#059669' : '#dc2626';
  if (abs >= 0.4) return r > 0 ? '#10b981' : '#f87171';
  return '#94a3b8';
}

function getCorrelationBg(r) {
  const abs = Math.abs(r);
  if (abs >= 0.7) return r > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800';
  if (abs >= 0.4) return r > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700';
  return 'bg-slate-100 text-slate-600';
}

function SignificanceBadge({ significant, pValue }) {
  if (significant) {
    return <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">p={pValue}</Badge>;
  }
  return <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500">p={pValue}</Badge>;
}

// --- Heatmap Matrix ---
function CorrelationHeatmap({ matrix, yKpis, onCellClick, lag }) {
  if (!matrix || matrix.length === 0) {
    return <div className="text-center text-slate-500 py-12">No correlation data available. Import LEI and KPI data first.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="sticky left-0 bg-white z-10 px-3 py-2 text-left font-medium text-slate-700 min-w-[160px]">
              Measure {lag > 0 && <span className="text-teal-600">(lag {lag}mo)</span>}
            </th>
            {yKpis.map(kpi => (
              <th key={kpi} className="px-2 py-2 text-center font-medium text-slate-700 min-w-[90px] whitespace-nowrap">
                {getLabel(kpi)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map(row => (
            <tr key={row.measure_id} className="border-t border-slate-100">
              <td className="sticky left-0 bg-white z-10 px-3 py-1.5 font-medium text-slate-800 whitespace-nowrap">
                {getLabel(row.measure_id)}
              </td>
              {yKpis.map(kpi => {
                const corr = row.correlations[kpi] || {};
                const r = corr.r || 0;
                const n = corr.n || 0;
                return (
                  <td
                    key={`${row.measure_id}-${kpi}`}
                    className="px-1 py-1 text-center cursor-pointer hover:ring-2 hover:ring-teal-400 transition-all rounded"
                    onClick={() => onCellClick(row.measure_id, kpi)}
                    data-testid={`heatmap-cell-${row.measure_id}-${kpi}`}
                    title={`r=${r}, p=${corr.p_value}, n=${n}`}
                  >
                    {n >= 3 ? (
                      <div
                        className="rounded px-1 py-0.5 font-mono font-bold text-xs mx-auto"
                        style={{
                          backgroundColor: `${getCorrelationColor(r)}18`,
                          color: getCorrelationColor(r),
                          border: corr.significant ? `1.5px solid ${getCorrelationColor(r)}` : '1px solid transparent',
                        }}
                      >
                        {r.toFixed(2)}
                      </div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-600 inline-block" /> Strong +</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-400 inline-block" /> Moderate +</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-300 inline-block" /> Weak</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-400 inline-block" /> Moderate -</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-600 inline-block" /> Strong -</span>
        <span className="ml-2">| <strong>Bold border</strong> = statistically significant (p &lt; 0.05)</span>
      </div>
    </div>
  );
}

// --- Detail Panel ---
function DetailPanel({ detail, onClose, onGenerateNarrative, narrativeLoading }) {
  const [tsViewMode, setTsViewMode] = React.useState('absolute');

  // Compute normalized time series data
  const normalizedTimeSeries = React.useMemo(() => {
    if (tsViewMode !== 'normalized' || !detail?.time_series?.length) return [];
    const baseX = detail.time_series.find(d => d.x_value != null)?.x_value;
    const baseY = detail.time_series.find(d => d.y_value != null)?.y_value;
    return detail.time_series.map(d => ({
      period: d.period,
      x_value: d.x_value != null && baseX ? ((d.x_value - baseX) / baseX) * 100 : null,
      y_value: d.y_value != null && baseY ? ((d.y_value - baseY) / baseY) * 100 : null,
    }));
  }, [detail?.time_series, tsViewMode]);

  if (!detail) return null;

  return (
    <Card className="mt-6" data-testid="correlation-detail-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            {getLabel(detail.x_measure)} <ChevronRight className="inline h-4 w-4 text-slate-400" /> {getLabel(detail.y_kpi)}
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onGenerateNarrative} disabled={narrativeLoading} data-testid="generate-narrative-btn">
              {narrativeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Brain className="h-4 w-4 mr-1" />}
              AI Analysis
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Lag Analysis */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-slate-700">Time-Lag Analysis (0-3 months)</h4>
            <div className="space-y-2">
              {detail.lag_analysis.map(lag => (
                <div
                  key={`lag-${lag.lag_months}`}
                  className={`flex items-center justify-between p-2.5 rounded-lg border transition-all ${
                    lag.lag_months === detail.best_lag.lag ? 'border-teal-300 bg-teal-50/50' : 'border-slate-100'
                  }`}
                  data-testid={`lag-${lag.lag_months}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono w-12">{lag.lag_months}mo lag</span>
                    <span className={`text-sm font-bold ${getCorrelationBg(lag.r)} px-2 py-0.5 rounded`}>
                      r = {lag.r.toFixed(4)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SignificanceBadge significant={lag.significant} pValue={lag.p_value} />
                    <span className="text-xs text-slate-400">n={lag.n}</span>
                    {lag.lag_months === detail.best_lag.lag && (
                      <Badge className="bg-teal-600 text-white text-xs">Best</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {detail.best_lag.lag > 0 && (
              <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <Info className="inline h-3.5 w-3.5 mr-1" />
                Strongest correlation at <strong>{detail.best_lag.lag} month lag</strong> — suggests LEI changes take ~{detail.best_lag.lag} months to reflect in KPI outcomes.
              </div>
            )}
          </div>

          {/* Scatter Plot */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-slate-700">Scatter Plot (lag 0)</h4>
            {detail.scatter_data.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="x" name={getLabel(detail.x_measure)} type="number" tick={{ fontSize: 10 }}
                    label={{ value: getLabel(detail.x_measure), position: 'bottom', offset: 5, fontSize: 10 }} />
                  <YAxis dataKey="y" name={getLabel(detail.y_kpi)} type="number" tick={{ fontSize: 10 }}
                    label={{ value: getLabel(detail.y_kpi), angle: -90, position: 'insideLeft', offset: 5, fontSize: 10 }} />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(val) => val.toFixed(2)} />
                  <Scatter data={detail.scatter_data} fill="#0d9488" fillOpacity={0.7} />
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-60 flex items-center justify-center text-slate-400 text-sm">No overlapping data points</div>
            )}
          </div>

          {/* Time Series */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-700">Time Series Overlay</h4>
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-100" data-testid="corr-ts-view-toggle">
                <button
                  onClick={() => setTsViewMode('absolute')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                    tsViewMode === 'absolute' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <BarChart2 className="h-3 w-3" />
                  Absolute
                </button>
                <button
                  onClick={() => setTsViewMode('normalized')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                    tsViewMode === 'normalized' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Percent className="h-3 w-3" />
                  % Change
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              {tsViewMode === 'normalized' ? (
                <LineChart data={normalizedTimeSeries} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
                  />
                  <Tooltip formatter={(val) => `${val >= 0 ? '+' : ''}${val?.toFixed(1)}%`} />
                  <Legend />
                  <Line type="monotone" dataKey="x_value" name={getLabel(detail.x_measure)}
                    stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="y_value" name={getLabel(detail.y_kpi)}
                    stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                </LineChart>
              ) : (
                <LineChart data={detail.time_series} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="x_value" name={getLabel(detail.x_measure)}
                    stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="y_value" name={getLabel(detail.y_kpi)}
                    stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Divergences Panel ---
function DivergencesPanel({ divergences }) {
  if (!divergences || divergences.length === 0) {
    return (
      <div className="text-center text-slate-500 py-8">
        <TrendingUp className="h-10 w-10 mx-auto mb-2 text-slate-300" />
        No significant divergences detected. LEI and KPI trends are broadly aligned.
      </div>
    );
  }

  const directionLabels = {
    x_up_y_down: 'LEI improving, KPI declining',
    x_down_y_up: 'LEI declining, KPI improving',
    x_moving_y_flat: 'LEI changing, KPI flat',
    y_moving_x_flat: 'KPI changing, LEI flat',
  };

  return (
    <div className="space-y-3">
      {divergences.map((d, i) => (
        <div key={`${d.x_measure}-${d.y_kpi}`} className="flex items-start gap-4 p-3 rounded-lg border border-slate-200 hover:border-amber-300 transition-all" data-testid={`divergence-${i}`}>
          <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${d.severity === 'high' ? 'text-rose-500' : 'text-amber-500'}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-slate-800">{getLabel(d.x_measure)}</span>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-medium text-sm text-slate-800">{getLabel(d.y_kpi)}</span>
              <Badge variant={d.severity === 'high' ? 'destructive' : 'outline'} className="text-xs">
                {d.severity}
              </Badge>
            </div>
            <p className="text-xs text-slate-600 mt-1">{directionLabels[d.direction] || d.direction}</p>
            <div className="flex gap-4 mt-1 text-xs text-slate-500">
              <span>LEI trend: <strong className={d.x_trend > 0 ? 'text-emerald-600' : 'text-rose-600'}>{(d.x_trend * 100).toFixed(1)}%/mo</strong></span>
              <span>KPI trend: <strong className={d.y_trend > 0 ? 'text-emerald-600' : 'text-rose-600'}>{(d.y_trend * 100).toFixed(1)}%/mo</strong></span>
              <span>r = {d.correlation?.r?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Narrative Panel ---
function NarrativePanel({ narrative }) {
  if (!narrative) return null;
  return (
    <Card className="mt-6 border-indigo-200" data-testid="narrative-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Brain className="h-5 w-5 text-indigo-500" /> AI Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{narrative}</div>
      </CardContent>
    </Card>
  );
}

// --- KPI Alignment Panel ---
const fmtMoney = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

// Compares the EXPECTED LEI-measure → KPI influence (27×10 strength
// matrix) against the OBSERVED empirical correlation. Surfaces the
// core Missing Middle signal: modelled-strong-but-unrealised "transfer
// gaps", confirmed drivers, and unexpected emergent drivers.
function AlignmentBar({ expected, observed }) {
  const exPct = Math.round((expected || 0) * 100);
  const obPct = Math.round((observed || 0) * 100);
  return (
    <div className="w-40 shrink-0">
      <div className="relative h-2 rounded-full bg-slate-100">
        <div className="absolute inset-y-0 left-0 rounded-full bg-indigo-300" style={{ width: `${exPct}%` }} title={`Expected ${exPct}%`} />
        <div className="absolute inset-y-0 left-0 rounded-full bg-teal-500" style={{ width: `${obPct}%`, opacity: 0.85 }} title={`Observed ${obPct}%`} />
      </div>
      <div className="mt-1 flex justify-between text-[9px] text-slate-400">
        <span>obs {obPct}%</span>
        <span>exp {exPct}%</span>
      </div>
    </div>
  );
}

function AlignmentPanel({ alignment, loading, onCellClick, onModelFix, orgName, orgId, trend, cohortName }) {
  const [selected, setSelected] = useState({});
  const [exporting, setExporting] = useState(false);
  const [briefs, setBriefs] = useState({});
  const [includeBriefs, setIncludeBriefs] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [groupBy, setGroupBy] = useState('lever');
  const [expandedKpis, setExpandedKpis] = useState({});
  const [alignmentTarget, setAlignmentTarget] = useState(60);

  useEffect(() => {
    let alive = true;
    const p = orgId ? `?organization_id=${orgId}` : '';
    api.get(`/modelling-settings/alignment-target${p}`)
      .then((r) => { if (alive && r?.data?.alignment_target != null) setAlignmentTarget(r.data.alignment_target); })
      .catch((e) => { logger.debug?.('load alignment target failed', e); });
    return () => { alive = false; };
  }, [orgId]);

  const saveTarget = async (val) => {
    try {
      await api.post('/modelling-settings/alignment-target', { alignment_target: val, organization_id: orgId || undefined });
    } catch (e) {
      logger.debug?.('save alignment target failed', e);
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div>;
  }
  if (!alignment || (alignment.total_pairs_evaluated || 0) === 0) {
    return <div className="text-center py-8 text-slate-500" data-testid="alignment-empty">No alignment data yet — import LEI measure history and KPI actuals to compare modelled vs realised influence.</div>;
  }

  const score = alignment.alignment_score;
  const scoreTone = score == null ? 'text-slate-400' : score >= 60 ? 'text-emerald-600' : score >= 30 ? 'text-amber-600' : 'text-rose-600';
  const gaps = alignment.transfer_gaps || [];
  const confirmed = alignment.confirmed_drivers || [];
  const emergent = alignment.emergent_drivers || [];
  const maxRecoverable = Math.max(1, ...gaps.map((g) => g.recoverable_value || 0));

  const keyOf = (c) => `${c.measure_id}-${c.kpi_id}`;
  const toggleSelect = (c) => setSelected((prev) => {
    const k = keyOf(c);
    const next = { ...prev };
    if (next[k]) delete next[k]; else next[k] = c;
    return next;
  });
  const selectedList = Object.values(selected);
  const selectMany = (cells) => setSelected((prev) => {
    const next = { ...prev };
    cells.forEach((c) => { next[keyOf(c)] = c; });
    return next;
  });
  const sumRecover = selectedList.reduce((s, c) => s + (c.recoverable_value || 0), 0);
  const sumRisk = selectedList.reduce((s, c) => s + (c.dollars_at_risk || 0), 0);
  const sumInvest = selectedList.reduce((s, c) => s + (c.estimated_investment || 0), 0);
  const blendedPayback = sumRecover > 0 ? (sumInvest / sumRecover) * 12 : null;

  const gapPayload = (c) => ({
    measure_label: getLabel(c.measure_id),
    kpi_label: getLabel(c.kpi_id),
    gap: c.gap,
    expected_strength: c.expected_strength,
    observed_abs_r: c.observed_abs_r,
    dollars_at_risk: c.dollars_at_risk,
    recoverable_value: c.recoverable_value,
    estimated_investment: c.estimated_investment,
    payback_months: c.payback_months,
  });

  const emailShortlist = async () => {
    if (!emailTo || !emailTo.includes('@')) { toast.error('Enter a valid recipient email'); return; }
    try {
      setEmailing(true);
      await api.post('/analysis/correlation/alignment/shortlist-email', {
        org_name: orgName || undefined,
        organization_id: orgId || undefined,
        workforce: alignment.workforce || undefined,
        alignment_target: alignmentTarget,
        current_score: alignment.alignment_score,
        cohort_name: cohortName || undefined,
        recipient_email: emailTo,
        gaps: selectedList.map(gapPayload),
      });
      toast.success(`Shortlist emailed to ${emailTo}`);
      setEmailTo('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to send email';
      logger.error('email shortlist failed:', err);
      toast.error(msg);
    } finally {
      setEmailing(false);
    }
  };

  const toggleBrief = async (c) => {
    const k = keyOf(c);
    const existing = briefs[k];
    if (existing?.open) { setBriefs((p) => ({ ...p, [k]: { ...p[k], open: false } })); return; }
    if (existing?.data) { setBriefs((p) => ({ ...p, [k]: { ...p[k], open: true } })); return; }
    setBriefs((p) => ({ ...p, [k]: { open: true, loading: true } }));
    try {
      const res = await api.post('/analysis/correlation/alignment/gap-brief', {
        measure_label: getLabel(c.measure_id),
        kpi_label: getLabel(c.kpi_id),
        expected: Math.round((c.expected_strength || 0) * 100),
        observed: Math.round((c.observed_abs_r || 0) * 100),
        gap: Math.round((c.gap || 0) * 100),
        dollars_at_risk: c.dollars_at_risk,
        recoverable_value: c.recoverable_value,
      });
      setBriefs((p) => ({ ...p, [k]: { open: true, loading: false, data: res.data } }));
    } catch (err) {
      logger.error('gap brief failed', err);
      setBriefs((p) => ({ ...p, [k]: { open: true, loading: false, error: true } }));
    }
  };

  const exportShortlist = async () => {
    try {
      setExporting(true);
      // When requested, make sure every selected gap has an AI brief so the
      // exported pack is self-explaining — generate any that are missing.
      let briefMap = {};
      if (includeBriefs) {
        const results = await Promise.all(selectedList.map(async (c) => {
          const k = keyOf(c);
          if (briefs[k]?.data) return [k, briefs[k].data];
          try {
            const r = await api.post('/analysis/correlation/alignment/gap-brief', {
              measure_label: getLabel(c.measure_id),
              kpi_label: getLabel(c.kpi_id),
              expected: Math.round((c.expected_strength || 0) * 100),
              observed: Math.round((c.observed_abs_r || 0) * 100),
              gap: Math.round((c.gap || 0) * 100),
              dollars_at_risk: c.dollars_at_risk,
              recoverable_value: c.recoverable_value,
            });
            return [k, r.data];
          } catch {
            return [k, null];
          }
        }));
        briefMap = Object.fromEntries(results);
      }
      const payload = {
        org_name: orgName || undefined,
        organization_id: orgId || undefined,
        workforce: alignment.workforce || undefined,
        alignment_target: alignmentTarget,
        current_score: alignment.alignment_score,
        cohort_name: cohortName || undefined,
        gaps: selectedList.map((c) => {
          const g = {
            measure_label: getLabel(c.measure_id),
            kpi_label: getLabel(c.kpi_id),
            gap: c.gap,
            expected_strength: c.expected_strength,
            observed_abs_r: c.observed_abs_r,
            dollars_at_risk: c.dollars_at_risk,
            recoverable_value: c.recoverable_value,
            estimated_investment: c.estimated_investment,
            payback_months: c.payback_months,
          };
          const bd = includeBriefs ? briefMap[keyOf(c)] : null;
          if (bd) { g.brief_why = bd.why; g.brief_moves = bd.moves; }
          return g;
        }),
      };
      const res = await api.post('/analysis/correlation/alignment/shortlist-pdf', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `capability-investment-shortlist-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Shortlist exported');
    } catch (err) {
      logger.error('Export shortlist failed:', err);
      toast.error('Failed to export shortlist');
    } finally {
      setExporting(false);
    }
  };

  const Row = ({ c, tone, selectable, onBrief, briefOpen, briefLoading }) => (
    <div
      className={cn(
        'w-full flex items-center justify-between gap-3 rounded-lg border p-3 hover:border-teal-300 transition-all',
        selectable && selected[keyOf(c)] && 'border-emerald-400 bg-emerald-50/40',
      )}
      data-testid={`alignment-row-${c.measure_id}-${c.kpi_id}`}
    >
      {selectable && (
        <Checkbox
          checked={Boolean(selected[keyOf(c)])}
          onCheckedChange={() => toggleSelect(c)}
          className="shrink-0 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
          data-testid={`alignment-select-${c.measure_id}-${c.kpi_id}`}
        />
      )}
      <button
        type="button"
        onClick={() => onCellClick(c.measure_id, c.kpi_id)}
        className="flex items-center gap-2 min-w-0 flex-1 text-left"
        title="Drill into this pair"
      >
        <span className="text-sm font-medium truncate">{getLabel(c.measure_id)}</span>
        <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className="text-sm font-medium truncate">{getLabel(c.kpi_id)}</span>
      </button>
      <div className="flex items-center gap-3 shrink-0">
        <AlignmentBar expected={c.expected_strength} observed={c.observed_abs_r} />
        <span className={`text-xs font-mono font-semibold ${tone}`}>Δ {(c.gap * 100).toFixed(0)}pp</span>
        {c.recoverable_value > 0 && (
          <div
            className="w-40 shrink-0"
            title={`Projected year-one recoverable value if you close this gap: ${fmtMoney(c.recoverable_value)} — about ${Math.round(c.capture_efficiency * 100)}% of the ${fmtMoney(c.dollars_at_risk)} at risk, using this lever's elasticity (${c.elasticity}) × execution dampening`}
            data-testid={`alignment-recover-${c.measure_id}-${c.kpi_id}`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-bold text-emerald-600">{fmtMoney(c.recoverable_value)}</span>
              <span className="text-[9px] font-semibold uppercase tracking-wide text-emerald-500">recoverable</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-emerald-100 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.round((c.recoverable_value / maxRecoverable) * 100)}%` }} />
            </div>
            <div className="mt-0.5 text-[9px] text-slate-400 text-right">of {fmtMoney(c.dollars_at_risk)} at risk</div>
          </div>
        )}
        <button
          type="button"
          onClick={() => onModelFix(c)}
          className="inline-flex items-center gap-1 rounded-full bg-teal-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-teal-700 transition-colors"
          data-testid={`alignment-model-fix-${c.measure_id}-${c.kpi_id}`}
          title={`Model interventions that convert ${getLabel(c.measure_id)} into ${getLabel(c.kpi_id)}`}
        >
          <Wrench className="h-3 w-3" /> Model the fix
        </button>
        {onBrief && (
          <button
            type="button"
            onClick={onBrief}
            className={cn(
              'inline-flex items-center justify-center h-7 w-7 rounded-full border transition-colors shrink-0',
              briefOpen ? 'border-indigo-400 bg-indigo-50 text-indigo-600' : 'border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-300',
            )}
            title="AI brief: why isn't this converting, and what to change?"
            data-testid={`alignment-brief-btn-${c.measure_id}-${c.kpi_id}`}
          >
            {briefLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="alignment-panel">
      {/* Scorecard headline */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-900 bg-slate-900 text-white sm:col-span-2 lg:col-span-1">
          <CardContent className="pt-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Annual $ at Risk</div>
            <div className="text-3xl font-bold text-amber-400" data-testid="alignment-dollars-total">{fmtMoney(alignment.total_dollars_at_risk)}</div>
            <div className="text-xs text-emerald-400 font-semibold mt-1" data-testid="alignment-recover-total">≈ {fmtMoney(alignment.total_recoverable_value)} recoverable in year one</div>
            <div className="text-xs text-slate-400 mt-0.5">Across all transfer gaps{alignment.workforce ? ` · ${alignment.workforce.toLocaleString()} workforce` : ''}</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2">
              Alignment Score
              {score != null && (
                alignmentTarget > score ? (
                  <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-bold px-1.5 py-px normal-case tracking-normal" data-testid="alignment-score-goal-chip">
                    {Math.round(alignmentTarget - score)} pts to {alignmentTarget}% goal
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-1.5 py-px normal-case tracking-normal" data-testid="alignment-score-goal-chip">
                    Goal met 🎯
                  </span>
                )
              )}
            </div>
            <div className={`text-3xl font-bold ${scoreTone}`} data-testid="alignment-score">{score == null ? '—' : `${score}%`}</div>
            <div className="text-xs text-slate-500 mt-1">{alignment.realised_pairs} of {alignment.strong_expected_pairs} strongly-modelled pairs realised in your data</div>
          </CardContent>
        </Card>
        <Card className="border-rose-200 bg-rose-50/40">
          <CardContent className="pt-4">
            <div className="text-xs uppercase tracking-wider text-rose-500 font-semibold flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" /> Transfer Gaps</div>
            <div className="text-3xl font-bold text-rose-600" data-testid="alignment-gap-count">{gaps.length}</div>
            <div className="text-xs text-slate-500 mt-1">Modelled to drive the KPI, but not yet showing in your results</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="pt-4">
            <div className="text-xs uppercase tracking-wider text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Confirmed Drivers</div>
            <div className="text-3xl font-bold text-emerald-600" data-testid="alignment-confirmed-count">{confirmed.length}</div>
            <div className="text-xs text-slate-500 mt-1">Strong expected influence confirmed by the data</div>
          </CardContent>
        </Card>
      </div>

      {/* Board-ready investment shortlist */}
      {selectedList.length > 0 && (
        <Card className="border-emerald-300 bg-emerald-50/60 shadow-sm" data-testid="alignment-shortlist">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                <Wrench className="h-4 w-4 text-emerald-600" /> Investment shortlist
                <span className="text-xs font-normal text-emerald-700">· {selectedList.length} gap{selectedList.length > 1 ? 's' : ''} selected</span>
              </h3>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 cursor-pointer select-none" title="Append each gap's AI diagnosis and moves to the exported PDF">
                  <Checkbox
                    checked={includeBriefs}
                    onCheckedChange={(v) => setIncludeBriefs(Boolean(v))}
                    className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                    data-testid="alignment-shortlist-include-briefs"
                  />
                  Include AI briefs
                </label>
                <button
                  type="button"
                  onClick={exportShortlist}
                  disabled={exporting}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-60 transition-colors"
                  data-testid="alignment-shortlist-export"
                >
                  {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />} {exporting && includeBriefs ? 'Writing briefs…' : 'Export shortlist'}
                </button>
                <button
                  type="button"
                  onClick={() => setSelected({})}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                  data-testid="alignment-shortlist-clear"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Recoverable / yr</div>
                <div className="text-2xl font-bold text-emerald-700" data-testid="alignment-shortlist-recover">{fmtMoney(sumRecover)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Est. investment</div>
                <div className="text-2xl font-bold text-slate-800" data-testid="alignment-shortlist-invest">{fmtMoney(sumInvest)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Blended payback</div>
                <div className="text-2xl font-bold text-slate-800" data-testid="alignment-shortlist-payback">{blendedPayback == null ? '—' : `${blendedPayback.toFixed(1)} mo`}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Covers $ at risk</div>
                <div className="text-2xl font-bold text-slate-800" data-testid="alignment-shortlist-risk">{fmtMoney(sumRisk)}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedList.map((c) => (
                <button
                  key={keyOf(c)}
                  type="button"
                  onClick={() => onModelFix(c)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white px-2.5 py-1 text-xs font-medium text-emerald-800 hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
                  title={`Open Solution Finder pre-filled for ${getLabel(c.measure_id)} → ${getLabel(c.kpi_id)}`}
                  data-testid={`alignment-shortlist-chip-${c.measure_id}-${c.kpi_id}`}
                >
                  <span className="truncate max-w-[180px]">{getLabel(c.measure_id)} → {getLabel(c.kpi_id)}</span>
                  <span className="font-semibold text-emerald-600">{fmtMoney(c.recoverable_value)}</span>
                  <Wrench className="h-3 w-3" />
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-emerald-200 pt-3">
              <span className="text-xs font-semibold text-slate-600">Email to CFO:</span>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="cfo@company.com"
                className="flex-1 min-w-[180px] rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:outline-none focus:border-emerald-400"
                data-testid="alignment-email-input"
              />
              <button
                type="button"
                onClick={emailShortlist}
                disabled={emailing}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                data-testid="alignment-email-send"
              >
                {emailing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Send PDF
              </button>
            </div>
            <p className="text-[11px] text-emerald-700/80 mt-3">Tap any chip to open Solution Finder pre-filled for that gap. Figures are modelled from your KPI value pools, measure elasticity and typical execution — a starting point for the board ask, not a commitment.</p>
          </CardContent>
        </Card>
      )}

      {/* Alignment trend (quarter-over-quarter) */}
      {trend?.points?.length > 1 && (
        <Card className="border-slate-200" data-testid="alignment-trend">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><BarChart2 className="h-4 w-4 text-indigo-500" /> Alignment trend <span className="text-xs font-normal text-slate-400">· quarter-over-quarter</span></h3>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600" title="Overlay a committed alignment-score goal on the trend">
                Target score
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={alignmentTarget}
                  onChange={(e) => setAlignmentTarget(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  onBlur={() => saveTarget(alignmentTarget)}
                  className="w-16 rounded-md border border-slate-200 px-2 py-1 text-xs text-right focus:outline-none focus:border-indigo-400"
                  data-testid="alignment-trend-target"
                />
                <span className="text-slate-400">%</span>
              </label>
            </div>
            <p className="text-xs text-slate-500 mb-3">Alignment metrics recomputed at each quarter-end from data up to that point — watch the score move and the $ at risk / recoverable shift as coverage grows and gaps close.</p>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend.points} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="money" tickFormatter={(v) => fmtMoney(v)} tick={{ fontSize: 10 }} width={52} />
                  <YAxis yAxisId="score" orientation="right" domain={[0, 100]} unit="%" tick={{ fontSize: 10 }} width={38} />
                  <Tooltip formatter={(value, name) => (name === 'Alignment score' ? [`${value}%`, name] : [fmtMoney(value), name])} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="money" dataKey="total_dollars_at_risk" name="$ at risk" fill="#fecdd3" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="money" dataKey="total_recoverable_value" name="Recoverable" fill="#6ee7b7" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="score" type="monotone" dataKey="alignment_score" name="Alignment score" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} />
                  {alignmentTarget > 0 && (
                    <ReferenceLine
                      yAxisId="score"
                      y={alignmentTarget}
                      stroke="#4f46e5"
                      strokeDasharray="4 4"
                      label={{ value: `Target ${alignmentTarget}%`, position: 'right', fill: '#4f46e5', fontSize: 10 }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {(() => {
              const latestPoint = trend.points[trend.points.length - 1];
              const latestScore = latestPoint?.alignment_score;
              if (latestScore == null) return null;
              const ptsToGoal = alignmentTarget > latestScore ? Math.round(alignmentTarget - latestScore) : 0;
              return (
                <div className="mt-2 text-xs" data-testid="alignment-trend-callout">
                  {ptsToGoal > 0 ? (
                    <span className="text-slate-600">You're <strong className="text-indigo-700">{ptsToGoal} pts</strong> below your <strong>{alignmentTarget}%</strong> goal (now {latestScore}%) — about <strong className="text-emerald-700">{fmtMoney(alignment.total_recoverable_value)}</strong> of recoverable value is on the table.</span>
                  ) : (
                    <span className="text-emerald-700 font-medium">On track — latest alignment {latestScore}% meets your {alignmentTarget}% goal. 🎯</span>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Transfer gaps — highest priority */}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Target className="h-4 w-4 text-rose-500" /> Biggest Transfer Gaps <span className="text-xs font-normal text-slate-400">· ranked by $ at risk</span></h3>
          <div className="inline-flex rounded-full border border-slate-200 p-0.5 bg-slate-50" data-testid="alignment-groupby">
            <button type="button" onClick={() => setGroupBy('lever')} className={cn('px-3 py-1 text-xs font-semibold rounded-full transition-colors', groupBy === 'lever' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500')} data-testid="alignment-groupby-lever">By lever</button>
            <button type="button" onClick={() => setGroupBy('kpi')} className={cn('px-3 py-1 text-xs font-semibold rounded-full transition-colors', groupBy === 'kpi' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500')} data-testid="alignment-groupby-kpi">By outcome (KPI)</button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">These learning measures are <strong>modelled to be strong levers</strong> for the KPI (indigo bar) but the realised correlation (teal bar) is weak — the clearest "learning isn't transferring" signal, ordered by the estimated annual value left on the table. Click any row to drill in, or "Model the fix" to pre-fill Solution Finder.</p>
        {gaps.length === 0 ? (
          <div className="text-center py-6 text-sm text-emerald-600">No transfer gaps — every strongly-modelled lever is showing through. 🎯</div>
        ) : groupBy === 'kpi' ? (
          <div className="space-y-2" data-testid="alignment-rollup-list">
            {(alignment.kpi_rollup || []).map((r) => {
              const maxRoll = Math.max(1, ...(alignment.kpi_rollup || []).map((x) => x.recoverable_value || 0));
              const levers = r.levers || [];
              const isOpen = Boolean(expandedKpis[r.kpi_id]);
              const allSelected = levers.length > 0 && levers.every((l) => selected[keyOf(l)]);
              const leverMax = Math.max(1, ...levers.map((l) => l.recoverable_value || 0));
              return (
                <div key={r.kpi_id} className="rounded-lg border" data-testid={`alignment-rollup-${r.kpi_id}`}>
                  <div className="w-full flex items-center justify-between gap-3 p-3">
                    <button
                      type="button"
                      onClick={() => setExpandedKpis((p) => ({ ...p, [r.kpi_id]: !p[r.kpi_id] }))}
                      className="flex items-center gap-2 min-w-0 flex-1 text-left"
                      data-testid={`alignment-rollup-toggle-${r.kpi_id}`}
                    >
                      <ChevronRight className={cn('h-4 w-4 text-slate-400 shrink-0 transition-transform', isOpen && 'rotate-90')} />
                      <span className="text-sm font-semibold truncate">{getLabel(r.kpi_id)}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-rose-600 bg-rose-50 rounded-full px-1.5 py-px">{r.gap_count} gaps</span>
                      <span className="text-[10px] text-slate-400">pool {fmtMoney(r.kpi_annual_value)}/yr</span>
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="w-40">
                        <div className="flex items-baseline justify-between">
                          <span className="text-sm font-bold text-emerald-600">{fmtMoney(r.recoverable_value)}</span>
                          <span className="text-[9px] font-semibold uppercase tracking-wide text-emerald-500">recoverable</span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-emerald-100 overflow-hidden">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.round((r.recoverable_value / maxRoll) * 100)}%` }} />
                        </div>
                        <div className="mt-0.5 text-[9px] text-slate-400 text-right">of {fmtMoney(r.dollars_at_risk)} at risk</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => selectMany(levers)}
                        className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors', allSelected ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-600 text-white hover:bg-emerald-700')}
                        data-testid={`alignment-rollup-shortlist-${r.kpi_id}`}
                        title={`Add all ${levers.length} levers for ${getLabel(r.kpi_id)} to the shortlist`}
                      >
                        {allSelected ? <CheckCircle2 className="h-3 w-3" /> : <Target className="h-3 w-3" />} {allSelected ? 'In shortlist' : `Shortlist all ${levers.length}`}
                      </button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="border-t bg-slate-50/50 px-3 py-2 space-y-1" data-testid={`alignment-rollup-levers-${r.kpi_id}`}>
                      {levers.map((l) => (
                        <div key={keyOf(l)} className="flex items-center justify-between gap-2 py-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <Checkbox
                              checked={Boolean(selected[keyOf(l)])}
                              onCheckedChange={() => toggleSelect(l)}
                              className="shrink-0 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                              data-testid={`alignment-rollup-select-${l.measure_id}-${l.kpi_id}`}
                            />
                            <span className="text-xs font-medium text-slate-700 truncate">{getLabel(l.measure_id)}</span>
                            <span className="text-[10px] font-mono text-rose-500">Δ {(l.gap * 100).toFixed(0)}pp</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="w-28 h-1.5 rounded-full bg-emerald-100 overflow-hidden">
                              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.round((l.recoverable_value / leverMax) * 100)}%` }} />
                            </div>
                            <span className="text-xs font-bold text-emerald-600 w-14 text-right">{fmtMoney(l.recoverable_value)}</span>
                            <button
                              type="button"
                              onClick={() => onModelFix(l)}
                              className="inline-flex items-center gap-1 rounded-full bg-teal-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-teal-700 transition-colors"
                              data-testid={`alignment-rollup-fix-${l.measure_id}-${l.kpi_id}`}
                            >
                              <Wrench className="h-2.5 w-2.5" /> Fix
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">{gaps.map((c) => {
            const k = keyOf(c);
            const b = briefs[k];
            return (
              <div key={k}>
                <Row c={c} tone="text-rose-600" selectable onBrief={() => toggleBrief(c)} briefOpen={b?.open} briefLoading={b?.loading} />
                {b?.open && (
                  <div className="mt-1 ml-8 mr-1 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3" data-testid={`alignment-brief-${c.measure_id}-${c.kpi_id}`}>
                    {b.loading ? (
                      <div className="flex items-center gap-2 text-xs text-indigo-600"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating brief…</div>
                    ) : b.error ? (
                      <div className="flex items-center justify-between text-xs text-rose-600">
                        <span>Couldn't generate a brief just now.</span>
                        <button type="button" onClick={() => toggleBrief(c)} className="font-semibold underline">Retry</button>
                      </div>
                    ) : b.data ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
                          <Sparkles className="h-3 w-3" /> AI transfer-gap brief
                          {b.data.ai === false && <span className="text-slate-400 normal-case font-normal">· offline heuristic</span>}
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{b.data.why}</p>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {(b.data.moves || []).map((m, i) => (
                            <div key={i} className="rounded-md bg-white border border-indigo-100 p-2">
                              <div className="text-xs font-semibold text-indigo-800">{m.title}</div>
                              <div className="text-xs text-slate-600 mt-0.5">{m.detail}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}</div>
        )}
      </div>

      {/* Confirmed drivers */}
      {confirmed.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Confirmed Drivers</h3>
          <div className="space-y-2">{confirmed.map(c => <Row key={`${c.measure_id}-${c.kpi_id}`} c={c} tone="text-emerald-600" />)}</div>
        </div>
      )}

      {/* Emergent drivers */}
      {emergent.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-indigo-500" /> Emergent Drivers (unexpected)</h3>
          <p className="text-xs text-slate-500 mb-3">Weak modelled influence but a strong observed correlation — worth investigating as a possible hidden lever.</p>
          <div className="space-y-2">{emergent.map(c => <Row key={`${c.measure_id}-${c.kpi_id}`} c={c} tone="text-indigo-600" />)}</div>
        </div>
      )}
    </div>
  );
}

// --- Cohort Compare ---
function CohortCompare({ cohorts, orgId }) {
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportCompare = async () => {
    try {
      setExporting(true);
      const res = await api.post('/analysis/correlation/alignment/compare-pdf',
        { organization_id: orgId || undefined, cohort_a: a, cohort_b: b },
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const el = document.createElement('a');
      el.href = url; el.download = `cohort-alignment-comparison-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(el); el.click(); window.URL.revokeObjectURL(url); document.body.removeChild(el);
      toast.success('Comparison exported');
    } catch (err) {
      logger.error('compare export failed', err);
      toast.error('Failed to export comparison');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!a || !b || a === b) { setData(null); return; }
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams({ cohort_a: a, cohort_b: b });
    if (orgId) params.set('organization_id', orgId);
    api.get(`/analysis/correlation/alignment/compare?${params}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => logger.error('cohort compare failed', e))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [a, b, orgId]);

  const Side = ({ s, other }) => {
    if (!s) return null;
    const worse = other && s.total_dollars_at_risk > other.total_dollars_at_risk;
    return (
      <div className={cn('rounded-lg border p-3', worse ? 'border-rose-300 bg-rose-50/40' : 'border-slate-200')} data-testid={`compare-side-${s.cohort_id}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold truncate">{s.cohort_name}</span>
          {worse && <span className="text-[10px] font-bold uppercase text-rose-600 bg-rose-100 rounded-full px-1.5 py-px">widest gap</span>}
        </div>
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div><div className="text-[9px] uppercase text-slate-400 font-semibold">Score</div><div className="text-lg font-bold text-slate-800">{s.alignment_score == null ? '—' : `${s.alignment_score}%`}</div></div>
          <div><div className="text-[9px] uppercase text-slate-400 font-semibold">$ at risk</div><div className="text-lg font-bold text-rose-600">{fmtMoney(s.total_dollars_at_risk)}</div></div>
          <div><div className="text-[9px] uppercase text-slate-400 font-semibold">Recoverable</div><div className="text-lg font-bold text-emerald-600">{fmtMoney(s.total_recoverable_value)}</div></div>
        </div>
        <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">Top gaps</div>
        <div className="space-y-1">
          {(s.top_gaps || []).map((g) => (
            <div key={`${g.measure_id}-${g.kpi_id}`} className="flex items-center justify-between text-xs">
              <span className="truncate">{getLabel(g.measure_id)} → {getLabel(g.kpi_id)}</span>
              <span className="font-semibold text-slate-700 shrink-0 ml-2">{fmtMoney(g.dollars_at_risk)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 mb-4" data-testid="cohort-compare">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-sm font-semibold text-indigo-900 flex items-center gap-2"><Shuffle className="h-4 w-4 text-indigo-500" /> Compare cohorts</span>
        <Select value={a} onValueChange={setA}>
          <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="compare-select-a"><SelectValue placeholder="Cohort A" /></SelectTrigger>
          <SelectContent>{cohorts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-slate-400">vs</span>
        <Select value={b} onValueChange={setB}>
          <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="compare-select-b"><SelectValue placeholder="Cohort B" /></SelectTrigger>
          <SelectContent>{cohorts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        {data && (
          <button
            type="button"
            onClick={exportCompare}
            disabled={exporting}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-60 transition-colors"
            data-testid="compare-export"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />} Export comparison
          </button>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-indigo-600"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Comparing…</div>
      ) : data ? (
        <div className="grid sm:grid-cols-2 gap-3"><Side s={data.a} other={data.b} /><Side s={data.b} other={data.a} /></div>
      ) : (
        <div className="text-xs text-slate-500">Pick two different cohorts to compare their alignment side-by-side.</div>
      )}
    </div>
  );
}

// --- Main Page ---
export default function CorrelationAnalysisPage() {
  const { isSuperAdmin, viewAsOrg } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [matrixData, setMatrixData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [divergences, setDivergences] = useState(null);
  const [alignment, setAlignment] = useState(null);
  const [alignmentLoading, setAlignmentLoading] = useState(true);
  const [alignmentTrend, setAlignmentTrend] = useState(null);
  const [alignmentCohort, setAlignmentCohort] = useState('');
  const [alignmentCohorts, setAlignmentCohorts] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [lag, setLag] = useState(0);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [explanations, setExplanations] = useState({});
  const [explainLoadingKey, setExplainLoadingKey] = useState(null);

  const fetchMatrix = useCallback(async (lagVal, orgId) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ lag: lagVal, include_pe: true });
      if (orgId) params.set('organization_id', orgId);
      const res = await api.get(`/analysis/correlation/matrix?${params}`);
      setMatrixData(res.data);
    } catch (err) {
      logger.error('Error loading correlation matrix:', err);
      toast.error('Failed to load correlation data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDivergences = useCallback(async (orgId) => {
    try {
      const params = new URLSearchParams({ threshold: '0.1', include_pe: 'true' });
      if (orgId) params.set('organization_id', orgId);
      const res = await api.get(`/analysis/correlation/divergences?${params}`);
      setDivergences(res.data.divergences);
    } catch (err) {
      logger.error('Error loading divergences:', err);
    }
  }, []);

  const fetchAlignment = useCallback(async (lagVal, orgId, cohortId) => {
    try {
      setAlignmentLoading(true);
      const params = new URLSearchParams({ lag: lagVal });
      if (orgId) params.set('organization_id', orgId);
      if (cohortId) params.set('cohort_id', cohortId);
      const res = await api.get(`/analysis/correlation/alignment?${params}`);
      setAlignment(res.data);
    } catch (err) {
      logger.error('Error loading KPI alignment:', err);
    } finally {
      setAlignmentLoading(false);
    }
  }, []);

  const fetchAlignmentTrend = useCallback(async (lagVal, orgId, cohortId) => {
    try {
      const params = new URLSearchParams({ lag: lagVal });
      if (orgId) params.set('organization_id', orgId);
      if (cohortId) params.set('cohort_id', cohortId);
      const res = await api.get(`/analysis/correlation/alignment/trend?${params}`);
      setAlignmentTrend(res.data);
    } catch (err) {
      logger.error('Error loading alignment trend:', err);
    }
  }, []);

  useEffect(() => {
    const params = selectedOrg ? `?organization_id=${selectedOrg}` : '';
    api.get(`/analysis/correlation/alignment/cohorts${params}`)
      .then((r) => setAlignmentCohorts(r?.data?.cohorts || []))
      .catch((e) => logger.debug?.('load cohorts failed', e));
    setAlignmentCohort('');
  }, [selectedOrg]);

  useEffect(() => {
    fetchMatrix(lag, selectedOrg);
    fetchDivergences(selectedOrg);
    fetchAlignment(lag, selectedOrg, alignmentCohort);
    fetchAlignmentTrend(lag, selectedOrg, alignmentCohort);
  }, [lag, selectedOrg, alignmentCohort, fetchMatrix, fetchDivergences, fetchAlignment, fetchAlignmentTrend]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/rbac/organizations').then(res => {
      setOrganizations(res.data.organizations || []);
    }).catch(() => {});
  }, [isSuperAdmin]);

  const handleCellClick = async (xMeasure, yKpi) => {
    try {
      const params = new URLSearchParams({ x_measure: xMeasure, y_kpi: yKpi, max_lag: '3', include_pe: 'true' });
      if (selectedOrg) params.set('organization_id', selectedOrg);
      const res = await api.get(`/analysis/correlation/detail?${params}`);
      setDetail(res.data);
      setNarrative(null);
    } catch (err) {
      logger.error('Error loading detail:', err);
      toast.error('Failed to load correlation detail');
    }
  };

  // Transfer gap → Solution Finder bridge. Turns a diagnosed alignment
  // gap (learning measure modelled to drive a KPI but not converting)
  // into a pre-filled Solution Finder flow: pre-selects the KPI and
  // carries the gap context so SF shows the "transfer gap" banner.
  const handleModelFix = (c) => {
    const params = new URLSearchParams({
      kpi: c.kpi_id,
      align_gap: '1',
      align_label: getLabel(c.measure_id),
      align_kpi_label: getLabel(c.kpi_id),
      align_expected: String(Math.round((c.expected_strength || 0) * 100)),
      align_observed: String(Math.round((c.observed_abs_r || 0) * 100)),
    });
    if (selectedOrg) params.set('organization_id', selectedOrg);
    navigate(`/solution-finder?${params.toString()}`);
  };

  const handleGenerateNarrative = async () => {
    if (!detail) return;
    setNarrativeLoading(true);
    try {
      const matrixSummary = [];
      if (matrixData?.matrix) {
        for (const row of matrixData.matrix) {
          for (const [kpi, corr] of Object.entries(row.correlations)) {
            if (corr.significant && Math.abs(corr.r) >= 0.4) {
              matrixSummary.push({ x: row.measure_id, y: kpi, r: corr.r, p: corr.p_value, n: corr.n });
            }
          }
        }
        matrixSummary.sort((a, b) => Math.abs(b.r) - Math.abs(a.r));
      }

      const res = await api.post('/analysis/correlation/narrative', {
        analysis_data: {
          matrix_summary: matrixSummary.slice(0, 15),
          divergences: divergences || [],
          lag_analysis: detail.lag_analysis,
          best_lag: detail.best_lag,
          focus_pair: { x: detail.x_measure, y: detail.y_kpi },
        },
        context: `Focused on ${getLabel(detail.x_measure)} vs ${getLabel(detail.y_kpi)}. Organization data.`,
      });
      setNarrative(res.data.narrative);
      toast.success('AI analysis generated');
    } catch (err) {
      logger.error('Error generating narrative:', err);
      toast.error('Failed to generate AI analysis');
    } finally {
      setNarrativeLoading(false);
    }
  };

  const handleExplainCorrelation = async (c, e) => {
    e.stopPropagation();
    const key = `${c.x}-${c.y}`;
    if (explanations[key]) {
      setExplanations(prev => { const next = { ...prev }; delete next[key]; return next; });
      return;
    }
    setExplainLoadingKey(key);
    try {
      const res = await api.post('/analysis/correlation/narrative', {
        analysis_data: {
          matrix_summary: [{ x: c.x, y: c.y, r: c.r, p: c.p_value, n: c.n }],
          divergences: [],
          lag_analysis: [],
        },
        context: `Explain ONLY the single correlation between "${getLabel(c.x)}" (LEI measure) and "${getLabel(c.y)}" (KPI). r=${c.r.toFixed(4)}, p=${c.p_value}, R²=${c.r_squared.toFixed(4)}, n=${c.n}. Write for an L&D leader. Cover: 1) What this relationship means in plain language 2) Why it matters for the business 3) How to act on it (specific next steps). Keep it to 3-4 short paragraphs. Do NOT use markdown headers — use plain flowing text.`,
      });
      setExplanations(prev => ({ ...prev, [key]: res.data.narrative }));
    } catch {
      toast.error('Failed to generate explanation');
    } finally {
      setExplainLoadingKey(null);
    }
  };

  // Summary stats
  const topCorrelations = [];
  if (matrixData?.matrix) {
    for (const row of matrixData.matrix) {
      for (const [kpi, corr] of Object.entries(row.correlations)) {
        if (corr.significant) {
          topCorrelations.push({ x: row.measure_id, y: kpi, ...corr });
        }
      }
    }
    topCorrelations.sort((a, b) => Math.abs(b.r) - Math.abs(a.r));
  }

  const __pageTheme = getLightSectionTheme('Navy');

  // Hero stats — promoted from the inline summary cards so the
  // single most-CFO-relevant numbers (count of significant
  // correlations, the strongest pairwise r, and the divergence
  // count) anchor the top of the page rather than competing with
  // body content.
  const strongest = topCorrelations.length > 0 ? topCorrelations[0] : null;
  const __heroStats = [
    {
      label: 'Significant Correlations',
      value: topCorrelations.length,
      hint: 'p < 0.05 across LEI × KPI grid',
      testId: 'corr-hero-stat-significant',
      whyPanel: {
        label: 'Significant Correlations',
        value: topCorrelations.length,
        valueSuffix: 'pairs',
        description: (
          'The number of LEI × KPI cell pairs where Pearson '
          + 'correlation reached statistical significance (p < 0.05). '
          + 'A higher count signals that learning measures and '
          + 'business outcomes are reliably moving together — at '
          + 'least in pattern.'
        ),
        formula: 'count(cells where p_value < 0.05)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Significance ≠ causation. A high count tells you the data is rich enough to draw conclusions, but you still need the magnitude (r) and the divergence count to know whether the relationship is healthy.' },
          { label: 'What to do', value: '', hint: topCorrelations.length === 0
              ? 'Import more KPI history before drawing conclusions — small samples rarely hit p < 0.05. Aim for 12+ months of monthly data per KPI.'
              : (topCorrelations.length < 5
                  ? 'You have a partial signal. Add more KPI columns and import deeper history to surface relationships outside your top measures.'
                  : 'Strong signal — drill into the Top Correlations tab to find the highest-leverage learning levers, and check Divergences to spot anti-patterns.'),
          },
        ],
        links: [
          { label: 'Open Top Correlations tab', path: '/correlation-analysis' },
          { label: 'Import more KPI data', path: '/data-management' },
        ],
      },
    },
    {
      label: 'Strongest r',
      value: strongest ? strongest.r.toFixed(3) : '—',
      hint: strongest ? `${getLabel(strongest.x)} → ${getLabel(strongest.y)}` : 'awaiting data',
      testId: 'corr-hero-stat-strongest',
      whyPanel: {
        label: 'Strongest Correlation (r)',
        value: strongest ? strongest.r.toFixed(3) : '—',
        description: (
          'Pearson r ranges from -1 to +1. The closer to ±1, the '
          + 'tighter the linear relationship. The strongest r in your '
          + 'matrix is the single biggest "lever" your learning '
          + 'investment is currently pulling on a business outcome.'
        ),
        formula: 'max(|r|) where p < 0.05',
        breakdown: [
          { label: 'How to consider it', value: '', hint: '|r| ≥ 0.7 is strong, 0.4–0.7 is moderate, 0.2–0.4 is weak, < 0.2 is noise. Negative r is fine — it just means the variables move in opposite directions (could be intentional, e.g. training vs error rate).' },
          { label: 'What to do', value: '', hint: strongest && Math.abs(strongest.r) >= 0.7
              ? `${getLabel(strongest.x)} is a strong lever for ${getLabel(strongest.y)}. Double down — this is where the next dollar of L&D budget compounds best. Build a business case that quantifies the ROI of pushing this measure further.`
              : strongest && Math.abs(strongest.r) >= 0.4
                ? `Moderate signal. Click the cell in the matrix to see the scatter plot — confirm there are no outliers driving the result, then plan an intervention to strengthen the relationship.`
                : 'Even your strongest pair is weak — review whether the right KPIs are being tracked. Often a missing time-lag is the culprit; try the 1- or 2-month lag selector.',
          },
        ],
        links: strongest ? [
          { label: 'Drill into this pair', path: '/correlation-analysis' },
          { label: 'See the full matrix', path: '/correlation-analysis' },
        ] : [
          { label: 'Import KPI data', path: '/data-management' },
        ],
      },
    },
    {
      label: 'Divergences',
      value: divergences?.length ?? '—',
      hint: 'opposing-direction pairs',
      testId: 'corr-hero-stat-divergences',
      whyPanel: {
        label: 'Divergences',
        value: divergences?.length ?? 0,
        valueSuffix: 'pairs',
        description: (
          'Divergences flag pairs where a learning measure is '
          + 'trending UP while the related business KPI is trending '
          + 'DOWN (or vice-versa). They are the early warning '
          + 'system for "learning isn\'t transferring to performance."'
        ),
        formula: 'count(pairs where slope(LEI) × slope(KPI) < 0 AND |Δ| > 5%)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Zero divergences is ideal. Even 1–2 divergences warrant investigation — they often surface a missing piece (manager reinforcement, on-the-job application, or a metric that doesn\'t actually depend on learning).' },
          { label: 'What to do', value: '', hint: (divergences?.length || 0) === 0
              ? 'Clean signal — every learning measure is moving in step with the business. Use this to anchor your CFO narrative ("our learning investment is showing through").'
              : (divergences?.length === 1
                  ? 'Open the single divergence in the Divergences tab. The most common root cause is application gap — training is learned but not applied.'
                  : `${divergences.length} divergences need attention. Open the Divergences tab and triage the top 3 — these are your highest-priority intervention targets this quarter.`),
          },
        ],
        links: [
          { label: 'Open Divergences tab', path: '/correlation-analysis' },
          { label: 'Open Anomaly Dashboard', path: '/anomalies' },
        ],
      },
    },
  ];

  // Hero actions — org filter (super admins only) + time-lag
  // selector. Live in the hero so they're always one glance from
  // the metrics they re-scope.
  const __heroActions = (
    <>
      {isSuperAdmin && organizations.length > 0 && (
        <Select value={selectedOrg || '_default'} onValueChange={v => setSelectedOrg(v === '_default' ? '' : v)}>
          <SelectTrigger className="w-44 h-9 bg-white border border-slate-200 text-slate-700 [&>span]:text-slate-700" data-testid="org-selector">
            <SelectValue placeholder="My Organization" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_default">My Organization</SelectItem>
            {organizations.map(org => (
              <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Select value={String(lag)} onValueChange={v => setLag(Number(v))}>
        <SelectTrigger className="w-32 h-9 bg-white border border-slate-200 text-slate-700 [&>span]:text-slate-700" data-testid="lag-selector">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">No lag</SelectItem>
          <SelectItem value="1">1 month lag</SelectItem>
          <SelectItem value="2">2 month lag</SelectItem>
          <SelectItem value="3">3 month lag</SelectItem>
        </SelectContent>
      </Select>
    </>
  );

  return (
    <PageShell
      theme={__pageTheme}
      stickyTitle="Correlation & Divergence Analysis"
      hero={(
        <PageHero
          title="Correlation & Divergence Analysis"
          description={(
            "Are your learning investments actually moving the needle on business outcomes? "
            + "This analysis uses Pearson correlation to measure the statistical strength between "
            + "LEI measures and KPIs, and divergence detection to flag where learning metrics and "
            + "business results are moving in opposite directions — a sign that knowledge isn't "
            + "transferring to performance. Time-lag analysis (0-3 months) reveals how long it "
            + "takes for training improvements to show up in operational results."
          )}
          theme={__pageTheme}
          icon={Shuffle}
          actions={__heroActions}
          stats={__heroStats}
          testId="correlation-analysis-hero"
        />
      )}
    >
    <div className="space-y-6 px-6 pb-6 pt-0" data-testid="correlation-analysis-page">
      {/* Main Content */}
      <Tabs defaultValue="matrix">
        <TabsList data-testid="correlation-tabs">
          <TabsTrigger value="matrix" data-testid="tab-matrix">Correlation Matrix</TabsTrigger>
          <TabsTrigger value="divergences" data-testid="tab-divergences">
            Divergences {divergences?.length > 0 && <Badge variant="destructive" className="ml-1.5 text-xs">{divergences.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="top" data-testid="tab-top">Top Correlations</TabsTrigger>
          <TabsTrigger value="alignment" data-testid="tab-alignment">
            KPI Alignment {alignment?.transfer_gaps?.length > 0 && <Badge variant="destructive" className="ml-1.5 text-xs">{alignment.transfer_gaps.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="matrix" className="mt-4">
          <p className="text-sm text-slate-500 mb-3 leading-relaxed">
            Each cell shows the Pearson <strong>r</strong> value between a learning measure (row) and a business KPI (column). Green indicates a positive relationship — as one rises, so does the other. Red indicates an inverse relationship. Bold borders mark statistically significant results (p &lt; 0.05). Click any cell to drill into the detail view with lag analysis and scatter plots.
          </p>
          <Card>
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div>
              ) : (
                <CorrelationHeatmap
                  matrix={matrixData?.matrix || []}
                  yKpis={matrixData?.y_kpis || []}
                  onCellClick={handleCellClick}
                  lag={lag}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="divergences" className="mt-4">
          <p className="text-sm text-slate-500 mb-3 leading-relaxed">
            Divergences highlight pairs where learning metrics and business outcomes are trending in <strong>opposite directions</strong> — for example, training scores improving while productivity declines. These are early warning signs that learning isn't translating into performance, and may indicate a gap in application, relevance, or reinforcement on the job.
          </p>
          <Card>
            <CardContent className="pt-4">
              <DivergencesPanel divergences={divergences} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="top" className="mt-4">
          <p className="text-sm text-slate-500 mb-3 leading-relaxed">
            The strongest statistically significant correlations, ranked by absolute <strong>r</strong> value. These are your highest-confidence relationships — use them to identify which learning measures have the most measurable impact on business KPIs. Click <strong>"What does this mean?"</strong> on any row for an AI-generated plain-language explanation of the relationship and how to act on it.
          </p>
          <Card>
            <CardContent className="pt-4">
              {topCorrelations.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No statistically significant correlations found.</div>
              ) : (
                <div className="space-y-2">
                  {topCorrelations.slice(0, 20).map(c => {
                    const key = `${c.x}-${c.y}`;
                    const isExplaining = explainLoadingKey === key;
                    const explanation = explanations[key];
                    return (
                      <div key={key} className="rounded-lg border hover:border-teal-300 transition-all">
                        <div
                          className="flex items-center justify-between p-3 cursor-pointer"
                          onClick={() => handleCellClick(c.x, c.y)}
                          data-testid={`top-corr-${c.x}-${c.y}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`font-mono font-bold text-sm px-2 py-0.5 rounded ${getCorrelationBg(c.r)}`}>
                              r = {c.r.toFixed(3)}
                            </span>
                            <span className="text-sm font-medium">{getLabel(c.x)}</span>
                            <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                            <span className="text-sm font-medium">{getLabel(c.y)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">R²={c.r_squared.toFixed(3)}</span>
                            <SignificanceBadge significant={c.significant} pValue={c.p_value} />
                            <span className="text-xs text-slate-400">n={c.n}</span>
                            <button
                              onClick={(e) => handleExplainCorrelation(c, e)}
                              disabled={isExplaining}
                              className="ml-1 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors disabled:opacity-50"
                              data-testid={`explain-btn-${c.x}-${c.y}`}
                            >
                              {isExplaining ? <Loader2 className="h-3 w-3 animate-spin" /> : <Brain className="h-3 w-3" />}
                              {explanation ? 'Hide' : 'What does this mean?'}
                            </button>
                          </div>
                        </div>
                        {explanation && (
                          <div className="px-4 pb-3 pt-0 border-t border-slate-100" data-testid={`explanation-${c.x}-${c.y}`}>
                            <div className="mt-3 p-3 rounded-lg bg-indigo-50/60 text-sm text-slate-700 leading-relaxed">
                              <FormattedNarrative text={explanation} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alignment" className="mt-4">
          <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
            <p className="text-sm text-slate-500 leading-relaxed flex-1 min-w-[300px]">
              This view compares the <strong>expected</strong> influence of each learning measure on a KPI — from the research-backed 27×10 LEI→KPI strength matrix — against the <strong>observed</strong> correlation in your own data. Where a measure is modelled to be a strong lever but the realised correlation is weak, you have a <strong>transfer gap</strong>: learning is happening but not converting to business results. This is the Missing Middle made measurable.
            </p>
            {alignmentCohorts.length > 1 && (
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowCompare((v) => !v)}
                  className={cn('inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors', showCompare ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-indigo-300')}
                  data-testid="alignment-compare-toggle"
                >
                  <Shuffle className="h-3 w-3" /> Compare cohorts
                </button>
                <span className="text-xs font-semibold text-slate-500">Population</span>
                <Select value={alignmentCohort || 'all'} onValueChange={(v) => setAlignmentCohort(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="alignment-cohort-select">
                    <SelectValue placeholder="All cohorts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="alignment-cohort-option-all">All cohorts</SelectItem>
                    {alignmentCohorts.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`alignment-cohort-option-${c.id}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {showCompare && alignmentCohorts.length > 1 && (
            <CohortCompare cohorts={alignmentCohorts} orgId={viewAsOrg?.id} />
          )}
          <Card>
            <CardContent className="pt-4">
              <AlignmentPanel alignment={alignment} loading={alignmentLoading} onCellClick={handleCellClick} onModelFix={handleModelFix} orgName={viewAsOrg?.name} orgId={viewAsOrg?.id} trend={alignmentTrend} cohortName={alignmentCohorts.find((c) => c.id === alignmentCohort)?.name} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Panel */}
      <DetailPanel
        detail={detail}
        onClose={() => { setDetail(null); setNarrative(null); }}
        onGenerateNarrative={handleGenerateNarrative}
        narrativeLoading={narrativeLoading}
      />

      {/* AI Narrative */}
      <NarrativePanel narrative={narrative} />
    </div>
    </PageShell>
  );
}
